import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import {
    collection,
    addDoc,
    deleteDoc,
    doc,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

const VIDEOS_COLLECTION = 'videos';

const VideosContext = createContext();

export const useVideos = () => {
    const context = useContext(VideosContext);
    if (!context) {
        throw new Error('useVideos must be used within a VideosProvider');
    }
    return context;
};

export const VideosProvider = ({ children }) => {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const videosQuery = query(collection(db, VIDEOS_COLLECTION), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(
            videosQuery,
            (snapshot) => {
                setVideos(snapshot.docs.map((docSnap) => ({ ...docSnap.data(), id: docSnap.id })));
                setLoading(false);
            },
            (err) => {
                console.error('Failed to load videos:', err);
                setError(err.message);
                setLoading(false);
            }
        );
        return unsubscribe;
    }, []);

    const getInstagramVideos = useCallback(
        () => videos.filter((v) => v.platform === 'instagram'),
        [videos]
    );

    const getYoutubeVideos = useCallback(
        () => videos.filter((v) => v.platform === 'youtube'),
        [videos]
    );

    const addVideo = useCallback(async (videoData) => {
        await addDoc(collection(db, VIDEOS_COLLECTION), { ...videoData, createdAt: serverTimestamp() });
    }, []);

    const deleteVideo = useCallback(async (videoId) => {
        await deleteDoc(doc(db, VIDEOS_COLLECTION, videoId));
    }, []);

    const value = useMemo(
        () => ({ videos, loading, error, getInstagramVideos, getYoutubeVideos, addVideo, deleteVideo }),
        [videos, loading, error, getInstagramVideos, getYoutubeVideos, addVideo, deleteVideo]
    );

    return <VideosContext.Provider value={value}>{children}</VideosContext.Provider>;
};
